import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchWorkblockReport, fetchWorkblockInsights, fetchChatHistoryReport, fetchGo2Brief } from '../utils/api';
import { formatInventoryDate } from '../utils/date';

interface WorkblocksModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  selectedDate: string;
}

type ReportType = 'go2brief' | 'insights' | 'workblock' | 'chat';

interface ReportData {
  content: string;
  loading: boolean;
  error: string | null;
}

const WorkblocksModal: React.FC<WorkblocksModalProps> = ({ 
  isOpen, 
  onClose, 
  userId, 
  selectedDate 
}) => {
  const [activeTab, setActiveTab] = useState<ReportType>('workblock');
  const [reports, setReports] = useState<Record<ReportType, ReportData>>({
    go2brief: { content: '', loading: false, error: null },
    insights: { content: '', loading: false, error: null },
    workblock: { content: '', loading: false, error: null },
    chat: { content: '', loading: false, error: null }
  });

  const updateReport = (type: ReportType, data: Partial<ReportData>) => {
    setReports(prev => ({
      ...prev,
      [type]: { ...prev[type], ...data }
    }));
  };

  const reportsRef = useRef(reports);
  reportsRef.current = reports;

  const fetchReport = useCallback(async (type: ReportType, force = false) => {
    if (reportsRef.current[type].content && !force) return; // Already loaded

    updateReport(type, { loading: true, error: null });

    try {
      let content: string;
      switch (type) {
        case 'go2brief':
          content = await fetchGo2Brief(userId, selectedDate);
          break;
        case 'workblock':
          content = await fetchWorkblockReport(userId, selectedDate);
          break;
        case 'insights':
          content = await fetchWorkblockInsights(userId, selectedDate);
          break;
        case 'chat':
          content = await fetchChatHistoryReport(userId, selectedDate);
          break;
      }
      updateReport(type, { content, loading: false });
    } catch (error: any) {
      const errorMessage = error.response?.status === 404 
        ? `No ${type} data found for this date`
        : `Failed to load ${type} data`;
      updateReport(type, { loading: false, error: errorMessage });
    }
  }, [userId, selectedDate]);

  // Clear all cached data when userId or selectedDate changes
  useEffect(() => {
    setReports({
      go2brief: { content: '', loading: false, error: null },
      insights: { content: '', loading: false, error: null },
      workblock: { content: '', loading: false, error: null },
      chat: { content: '', loading: false, error: null }
    });
  }, [userId, selectedDate]);

  useEffect(() => {
    if (isOpen && activeTab) {
      fetchReport(activeTab);
    }
  }, [isOpen, activeTab, fetchReport]);

  const handleTabChange = (tab: ReportType) => {
    setActiveTab(tab);
    fetchReport(tab);
  };

  const formatDate = (date: string) => formatInventoryDate(date);

  const currentReport = reports[activeTab];

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="workblocks-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>Generated Files</h2>
            <p>User {userId} • {formatDate(selectedDate)}</p>
          </div>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`modal-tab ${activeTab === 'workblock' ? 'active' : ''}`}
            onClick={() => handleTabChange('workblock')}
          >
            Workblock Report (Raw)
          </button>
          <button
            className={`modal-tab ${activeTab === 'insights' ? 'active' : ''}`}
            onClick={() => handleTabChange('insights')}
          >
            Workblock Insights (AI Enhanced)
          </button>
          <button
            className={`modal-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => handleTabChange('chat')}
          >
            Chat History
          </button>
          <button
            className={`modal-tab ${activeTab === 'go2brief' ? 'active' : ''}`}
            onClick={() => handleTabChange('go2brief')}
          >
            Brief
          </button>
        </div>

        <div className="modal-content">
          {currentReport.loading && (
            <div className="loading-state">
              <div className="loading-spinner">⏳</div>
              <p>Loading {activeTab} data...</p>
            </div>
          )}

          {currentReport.error && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p>{currentReport.error}</p>
              <button 
                className="retry-btn"
                onClick={() => fetchReport(activeTab, true)}
              >
                Retry
              </button>
            </div>
          )}

          {currentReport.content && !currentReport.loading && (
            <div className="report-content">
              <pre>{currentReport.content}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkblocksModal;
